'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { Button } from '../../actions/button/index.js';
import { Combobox } from '../../forms/combobox/index.js';
import { Field, FieldError, FieldLabel } from '../../forms/form-field/index.js';
import { Input } from '../../forms/text-field/index.js';
const defaultMessages = {
    loadFailed: (what) => `Couldn’t load ${what}.`,
    retry: 'Try again',
    chooseCountryFirst: 'Choose a country first',
    typeMore: (min) => `Type at least ${min} letters to search.`,
    noMatch: 'No matches',
    useTyped: 'Not in the list: use as typed',
    optional: 'Optional',
};
const defaultLabels = {
    country: 'Country',
    region: 'State / Region',
    city: 'City',
    postalCode: 'Postal code',
};
const EMPTY = {
    country: null,
    region: null,
    regionName: '',
    city: '',
    cityId: null,
    postalCode: '',
};
const TYPED = '\u0000typed:';
const ALL_FIELDS = ['country', 'region', 'city', 'postalCode'];
function LoadFailed({ text, retry, onRetry, }) {
    return (_jsxs("div", { role: "alert", className: "mn-location-fields__failed", children: [_jsx("span", { children: text }), _jsx(Button, { size: "xs", variant: "secondary", onClick: onRetry, children: retry })] }));
}
/**
 * Country, region, city and postal code as one composite. Lists come from an
 * injected adapter, so the product owns the data and the requests. Picking a
 * country loads its regions under the country's own word; cities are searched
 * as the user types, can be typed freely, and fill in their region.
 */
export const LocationFields = forwardRef(function LocationFields({ adapter, value: valueProp, defaultValue, onValueChange, fields = ALL_FIELDS, labels: labelsProp, required = [], errors = {}, disabled = false, legend, cityMinQuery = 2, cityDebounce = 250, allowCustomCity = true, messages: messagesProp, idPrefix, className, ...rest }, ref) {
    const messages = { ...defaultMessages, ...messagesProp };
    const labels = { ...defaultLabels, ...labelsProp };
    const [internal, setInternal] = useState({ ...EMPTY, ...defaultValue });
    const value = valueProp ?? internal;
    const adapterRef = useRef(adapter);
    adapterRef.current = adapter;
    const update = (patch, changed) => {
        const next = { ...value, ...patch };
        if (valueProp === undefined)
            setInternal(next);
        onValueChange?.(next, changed);
    };
    // Countries: once on mount, and again on Retry.
    const [countries, setCountries] = useState({ state: 'loading' });
    const [countriesTry, setCountriesTry] = useState(0);
    useEffect(() => {
        const ctl = new AbortController();
        setCountries({ state: 'loading' });
        adapterRef.current
            .countries(ctl.signal)
            .then((data) => !ctl.signal.aborted && setCountries({ state: 'ready', data }))
            .catch(() => !ctl.signal.aborted && setCountries({ state: 'failed' }));
        return () => ctl.abort();
    }, [countriesTry]);
    // Regions: whenever the country changes.
    const [regions, setRegions] = useState({ state: 'idle' });
    const [regionsTry, setRegionsTry] = useState(0);
    const hasRegionSource = !!adapter.regions;
    useEffect(() => {
        const load = adapterRef.current.regions;
        if (!value.country || !load) {
            setRegions({ state: 'idle' });
            return;
        }
        const ctl = new AbortController();
        setRegions({ state: 'loading' });
        load(value.country, ctl.signal)
            .then((data) => !ctl.signal.aborted && setRegions({ state: 'ready', data }))
            .catch(() => !ctl.signal.aborted && setRegions({ state: 'failed' }));
        return () => ctl.abort();
    }, [value.country, regionsTry, hasRegionSource]);
    // Cities: searched as the user types; a chosen region is listed right away.
    const [cityText, setCityText] = useState(value.city);
    const [cities, setCities] = useState([]);
    const [citiesState, setCitiesState] = useState('idle');
    const cityAbort = useRef(null);
    const cityTimer = useRef(null);
    const skipQuery = useRef(null);
    useEffect(() => setCityText(value.city), [value.city]);
    const searchCities = useCallback((text, wait) => {
        if (cityTimer.current !== null)
            clearTimeout(cityTimer.current);
        cityAbort.current?.abort();
        const load = adapterRef.current.cities;
        const query = text.trim();
        if (!load || !value.country) {
            setCities([]);
            setCitiesState('idle');
            return;
        }
        if (!value.region && query.length < cityMinQuery) {
            setCities([]);
            setCitiesState('short');
            return;
        }
        const country = value.country;
        const region = value.region;
        cityTimer.current = setTimeout(() => {
            const ctl = new AbortController();
            cityAbort.current = ctl;
            setCitiesState('loading');
            load({ country, region, query }, ctl.signal)
                .then((data) => {
                if (ctl.signal.aborted)
                    return;
                setCities(data);
                setCitiesState('idle');
            })
                .catch(() => {
                if (ctl.signal.aborted)
                    return;
                setCities([]);
                setCitiesState('failed');
            });
        }, wait);
    }, [value.country, value.region, cityMinQuery]);
    // A new country or region invalidates the city list; list a chosen region's cities at once.
    useEffect(() => {
        searchCities('', 0);
        return () => {
            if (cityTimer.current !== null)
                clearTimeout(cityTimer.current);
            cityAbort.current?.abort();
        };
    }, [searchCities]);
    const countryOptions = useMemo(() => {
        if (countries.state === 'ready')
            return countries.data;
        return value.country ? [{ value: value.country, label: value.country }] : [];
    }, [countries, value.country]);
    const regionList = useMemo(() => (regions.state === 'ready' ? regions.data.options : []), [regions]);
    const regionLabel = (regions.state === 'ready' ? regions.data.label : undefined) ?? labels.region;
    const regionIsList = hasRegionSource && !!value.country && !(regions.state === 'ready' && regionList.length === 0);
    const regionOptions = useMemo(() => {
        const opts = [...regionList];
        if (value.region && !opts.some((o) => o.value === value.region))
            opts.push({ value: value.region, label: value.regionName || value.region });
        return opts;
    }, [regionList, value.region, value.regionName]);
    const cityValue = value.cityId ?? (value.city ? TYPED + value.city : null);
    const cityOptions = useMemo(() => {
        const opts = cities.map((c) => c.description === undefined
            ? { value: c.value, label: c.label }
            : { value: c.value, label: c.label, description: c.description });
        if (cityValue && !opts.some((o) => o.value === cityValue))
            opts.push({ value: cityValue, label: value.city });
        const typed = cityText.trim();
        if (allowCustomCity &&
            citiesState !== 'short' &&
            typed !== '' &&
            typed !== value.city &&
            !opts.some((o) => o.label.toLowerCase() === typed.toLowerCase()))
            opts.push({ value: TYPED + typed, label: typed, description: messages.useTyped });
        return opts;
    }, [cities, cityValue, value.city, cityText, citiesState, allowCustomCity, messages.useTyped]);
    const cityEmpty = citiesState === 'failed'
        ? messages.loadFailed('cities')
        : citiesState === 'short'
            ? messages.typeMore(cityMinQuery)
            : messages.noMatch;
    const base = idPrefix ?? undefined;
    const fieldId = (f) => (base ? `${base}-${f}` : undefined);
    const isRequired = (f) => required.includes(f);
    const renderField = (f) => {
        const error = errors[f];
        const label = f === 'region' ? regionLabel : labels[f];
        const noCountry = !value.country;
        let control;
        switch (f) {
            case 'country':
                control =
                    countries.state === 'failed' ? (_jsx(LoadFailed, { text: messages.loadFailed('countries'), retry: messages.retry, onRetry: () => setCountriesTry((n) => n + 1) })) : (_jsx(Combobox, { "aria-label": label, options: countryOptions, value: value.country, loading: countries.state === 'loading', onValueChange: (code) => {
                            if (code === value.country)
                                return;
                            setCityText('');
                            update({ ...EMPTY, country: code }, 'country');
                        } }));
                break;
            case 'region':
                control =
                    regions.state === 'failed' ? (_jsx(LoadFailed, { text: messages.loadFailed(regionLabel.toLowerCase()), retry: messages.retry, onRetry: () => setRegionsTry((n) => n + 1) })) : regionIsList ? (_jsx(Combobox, { "aria-label": label, options: regionOptions, value: value.region, loading: regions.state === 'loading', onValueChange: (code) => {
                            const opt = regionOptions.find((o) => o.value === code);
                            setCityText('');
                            update({ region: code, regionName: opt?.label ?? '', city: '', cityId: null }, 'region');
                        } })) : (_jsx(Input, { value: value.regionName, disabled: noCountry && fields.includes('country') ? true : undefined, placeholder: noCountry && fields.includes('country')
                            ? messages.chooseCountryFirst
                            : isRequired('region')
                                ? undefined
                                : messages.optional, autoComplete: "address-level1", onChange: (e) => update({ regionName: e.target.value, region: null }, 'region') }));
                break;
            case 'city':
                control = adapter.cities ? (_jsx(Combobox, { "aria-label": label, options: cityOptions, value: cityValue, inputValue: cityText, filter: () => true, loading: citiesState === 'loading', emptyMessage: cityEmpty, disabled: noCountry ? true : undefined, placeholder: noCountry ? messages.chooseCountryFirst : undefined, onInputValueChange: (text) => {
                        setCityText(text);
                        if (skipQuery.current === text) {
                            skipQuery.current = null;
                            return;
                        }
                        if (text === value.city)
                            return;
                        searchCities(text, cityDebounce);
                    }, onValueChange: (next) => {
                        if (next === null) {
                            skipQuery.current = '';
                            update({ city: '', cityId: null }, 'city');
                            return;
                        }
                        if (next.startsWith(TYPED)) {
                            const typed = next.slice(TYPED.length);
                            skipQuery.current = typed;
                            update({ city: typed, cityId: null }, 'city');
                            return;
                        }
                        const city = cities.find((c) => c.value === next);
                        if (!city)
                            return;
                        skipQuery.current = city.label;
                        update({
                            city: city.label,
                            cityId: city.value,
                            ...(city.region && !value.region
                                ? { region: city.region.value, regionName: city.region.label }
                                : {}),
                        }, 'city');
                    } })) : (_jsx(Input, { value: value.city, autoComplete: "address-level2", onChange: (e) => update({ city: e.target.value, cityId: null }, 'city') }));
                break;
            case 'postalCode':
                control = (_jsx(Input, { value: value.postalCode, autoComplete: "postal-code", autoCapitalize: "characters", spellCheck: false, onChange: (e) => update({ postalCode: e.target.value }, 'postalCode') }));
                break;
        }
        const id = fieldId(f);
        return (_jsxs(Field, { ...(id ? { id } : {}), invalid: !!error, required: isRequired(f), disabled: disabled, className: "mn-location-fields__field", "data-field": f, children: [_jsx(FieldLabel, { children: label }), control, _jsx(FieldError, { children: error })] }, f));
    };
    return (_jsxs("fieldset", { ref: ref, ...rest, disabled: disabled || undefined, className: cx('mn-location-fields', className), children: [legend ? _jsx("legend", { className: "mn-location-fields__legend", children: legend }) : null, _jsx("div", { className: "mn-location-fields__grid", children: fields.map(renderField) })] }));
});
//# sourceMappingURL=location-fields.js.map